import { SEOHead } from "@/components/SEOHead";
import Calculator from "@/components/Calculator";

const calculatorSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Sask Roofing Company Cost Calculator",
  url: "https://www.saskroofingcompany.com/calculator",
  description:
    "Free instant estimate of roof replacement cost based on Saskatchewan market rates. Adjust home size, material, pitch, and tear-off to get a personalized range.",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Any (web browser)",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "CAD",
  },
  provider: { "@id": "https://www.saskroofingcompany.com/#localbusiness" },
};

export default function CalculatorPage() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Roof Replacement Cost Calculator | Sask Roofing Company"
        description="Get an instant, rough estimate for your new roof based on Saskatchewan market rates. Use our free roof replacement cost calculator."
        canonical="/calculator"
        schemaJson={[calculatorSchema]}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-primary text-primary-foreground">
        <div className="container px-4 text-center max-w-3xl mx-auto space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            Roofing Cost <span className="text-accent">Calculator</span>
          </h1>
          <p className="text-lg md:text-xl text-primary-foreground/90">
            Get an idea of what a new roof might cost in Saskatchewan before
            booking your free inspection.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container px-4">
          <Calculator />
        </div>
      </section>

      {/* Context Section */}
      <section className="py-16 bg-muted/50 border-t">
        <div className="container px-4 max-w-4xl mx-auto space-y-8">
          <h2 className="text-3xl font-bold text-center">
            What Affects the Cost of a New Roof?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <h3 className="text-xl font-bold">1. Size and Pitch</h3>
              <p className="text-muted-foreground">
                Larger roofs require more materials and labor. Steeper roofs
                (high pitch) are more dangerous and take longer to install,
                increasing labor costs.
              </p>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-bold">2. Materials Used</h3>
              <p className="text-muted-foreground">
                Architectural asphalt shingles are the standard, but upgrading
                to premium metal roofing or specialized impact-resistant
                shingles will increase the initial price (though they last
                longer).
              </p>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-bold">3. Tear-off and Disposal</h3>
              <p className="text-muted-foreground">
                Removing the old roof layers adds labor time and dump fees. In
                Saskatchewan, it's highly recommended to tear off old shingles
                to inspect the decking.
              </p>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-bold">4. Decking and Repairs</h3>
              <p className="text-muted-foreground">
                If the plywood decking underneath is rotten from leaks or poor
                ventilation, it must be replaced before new shingles go on. This
                is an unpredictable variable until the old roof is off.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
